import type { LucideIcon } from 'lucide-react'
import {
  Home,
  BarChart3,
  Shield,
  Sparkles,
  CreditCard,
  GitCompareArrows,
  Plug,
  Database,
  Workflow,
  Bot,
  Server,
  Activity,
  Webhook,
  MailOpen,
  Cable,
  LayoutDashboard,
  ListTree,
  FileText,
  ArrowLeftRight,
  Scale,
  Landmark,
  TrendingUp,
  Blocks,
  Receipt,
  Tag,
  Settings,
  FileSignature,
  HandCoins,
  PiggyBank,
  Hammer,
  CheckCircle,
  GitMerge,
  Link,
  Layers,
  Send,
  ShieldCheck,
  ClipboardCheck,
  ScrollText,
  Users,
  UsersRound,
  KeyRound,
  Building2,
  Building,
  SlidersHorizontal,
  Milestone,
  Download,
  AlertTriangle,
  BellRing,
  MessageCircle,
  Wallet,
  Banknote,
  BookOpenCheck,
  FileStack,
  FileCog,
  FolderInput,
  Waypoints,
  Handshake,
  FileSpreadsheet,
} from 'lucide-react'
import { Permissions, PermissionLevel } from '@/shared/lib/permissions/permission-values'
import type { PermissionCheck } from '@/shared/lib/permissions/permission-values'
import type { AuthUser } from '@/shared/lib/auth/auth-provider'
import { reportingLevel } from '@/features/datalake-dashboards/report-access'
import { useAiDraftsCount } from '@/features/templates/hooks/use-ai-drafts-count'
import { useOpenConflictPairsCount } from '@/features/tagging/lib/use-conflicts-queries'

/** Something waiting behind a menu item: a number on the item and a dot on its section. */
export interface NavItemCount {
  /** A hook: rendered by the menu's count components, never called outside one. */
  useValue: () => number | undefined
  /** Plural i18n key of what a screen reader hears, with `{{count}}` ("3 proposals to review"). */
  ariaLabelKey: string
}

export interface NavItem {
  key: string
  labelKey: string
  path: string
  icon?: LucideIcon
  permission?: PermissionCheck
  /**
   * Alternative to `permission`: restrict the item to Admin / SuperAdmin users.
   * Use when the backend does not expose a granular permission string for the
   * page (e.g. Custom Entities) but the product rule is "admin only".
   */
  requireAdmin?: boolean
  /**
   * Alternative to `permission` when the page's own rule differs from `canAccess` (which lets only
   * SuperAdmin through): the menu then shows exactly who the route lets in. No dev bypass.
   */
  visibleFor?: (user: AuthUser | null) => boolean
  status?: 'active' | 'inactive'
  /** Renders a sub-header divider before this item within a mega-menu column */
  subheaderKey?: string
  /** i18n key with comma-separated search synonyms */
  searchTagsKey?: string
  /**
   * Restrict visibility by tenant:
   * - `'infra'`      → only the infra tenant (TenantId 41).
   * - `'recon-real'` → only tenants on the real reconciliation module
   *                    (RECONCILIATION_REAL_TENANT_IDS, e.g. 40).
   * - `'recon-mock'` → every tenant EXCEPT those on the real module (they see
   *                    the legacy mock/"upgrade" reconciliation screens).
   * - `'ecobank'`    → only the Ecobank demo tenant (ECOBANK_TENANT_ID, 12).
   * - `'datalake'`   → only tenants on the datalake dashboards POC
   *                    (DATALAKE_DASHBOARDS_TENANT_IDS, UC-100).
   */
  tenantOnly?: 'infra' | 'recon-real' | 'recon-mock' | 'ecobank' | 'datalake'
  /** Marks the item as a tenant-specific custom report; renders a "Custom" pill. */
  custom?: boolean
  /** A counter of what waits on this page (spec item 1: proposals to review on Ingestion). */
  count?: NavItemCount
}

export interface NavGroup {
  key: string
  labelKey: string
  icon: LucideIcon
  items: NavItem[]
}

export interface NavSection {
  key: string
  labelKey: string
  icon: LucideIcon
  /** Direct link — no dropdown, navigates on click */
  path?: string
  /** Status badge for direct-link sections */
  status?: 'active' | 'inactive'
  /**
   * Direct-link sections only: restrict the section to SuperAdmin users.
   * Stricter than `NavItem.requireAdmin` — tenant Admins do NOT see it.
   */
  requireSuperAdmin?: boolean
  /** Direct-link sections only: granular permission check, as on `NavItem`. */
  permission?: PermissionCheck
  /** Sub-groups rendered as columns in a mega-menu */
  groups?: NavGroup[]
  /** Flat item list — simple single-column dropdown */
  items?: NavItem[]
}

// ─── Top-level navigation sections ─────────────────────
// Order: Home · Roadmap · Status board · Integrations · Reconciliations · Reporting
// · Payments · Data · Workflows · Alerts · Agents · Admin Console · Settings

/** Same rule as the saved-reports route and the gateway: a tenant Admin counts as Admin (UC-104). */
function canOpenSavedReports(user: AuthUser | null): boolean {
  return reportingLevel(user) >= PermissionLevel.Read
}

export const NAV_SECTIONS: NavSection[] = [
  // ── Home (direct link) ────────────────────────────────
  {
    key: 'home',
    labelKey: 'nav.home',
    icon: Home,
    path: '/dashboard',
    status: 'active',
  },

  // Roadmap was removed from the menu on 2026-08-31 (user request): the page
  // and its /roadmap route still exist, it just is not offered in navigation.

  // ── Status board (direct link) ────────────────────────
  // UC-048. Marked 'active' because the page renders real backend data —
  // 'inactive' in this repo means mock/demo content, which this is not.
  // Open to every authenticated user (UC-092, reverses the
  // `Platform.OnboardingRoadmapBoard` gate of UC-084.6). Only the maintainer
  // console inside the page is super-admin only (UC-050).
  {
    key: 'statusBoard',
    labelKey: 'nav.statusBoard',
    icon: Milestone,
    path: '/status-board',
    status: 'active',
  },

  // ── Integrations ───────────────────────────────────────
  {
    key: 'integrations',
    labelKey: 'nav.integrations',
    icon: Plug,
    items: [
      { key: 'intServices', labelKey: 'nav.services', path: '/integrations/services', icon: Server, status: 'active', permission: { name: Permissions.Connectivity, level: PermissionLevel.Read }, searchTagsKey: 'searchTags.intServices' },
      { key: 'intStatus', labelKey: 'nav.intStatus', path: '/integrations/status', icon: Activity, status: 'inactive', searchTagsKey: 'searchTags.intStatus' },
      { key: 'intWebhooks', labelKey: 'nav.webhooks', path: '/integrations/webhooks', icon: Webhook, status: 'inactive', searchTagsKey: 'searchTags.intWebhooks' },
      { key: 'intEmailIngestion', labelKey: 'nav.emailIngestion', path: '/integrations/email-ingestion', icon: MailOpen, status: 'active', searchTagsKey: 'searchTags.intEmailIngestion' },
      { key: 'intApiAgent', labelKey: 'nav.apiAgentIntegrator', path: '/integrations/api-agent', icon: Cable, status: 'inactive', searchTagsKey: 'searchTags.intApiAgent' },
      { key: 'intSftp', labelKey: 'nav.sftpIngestion', path: '/integrations/sftp', icon: FolderInput, status: 'active', searchTagsKey: 'searchTags.intSftp' },
      { key: 'intServiceRules', labelKey: 'nav.serviceRules', path: '/integrations/service-rules', icon: FileCog, status: 'active', searchTagsKey: 'searchTags.intServiceRules' },
      { key: 'rptIngestion', labelKey: 'nav.ingestion', path: '/reporting/ingestion', icon: Activity, status: 'active', searchTagsKey: 'searchTags.rptIngestion', count: { useValue: useAiDraftsCount, ariaLabelKey: 'ingestionAi.pendingReviewCount' } },
    ],
  },

  // ── Reconciliations (mega-menu: 2 columns) ───────────
  // UC-099: "Administration" holds every screen that works the reconciliation;
  // "Reporting" holds the read-only reports. A column whose items are all
  // hidden for the tenant (real vs mock module) is not rendered.
  {
    key: 'reconciliation',
    labelKey: 'nav.reconciliation',
    icon: GitCompareArrows,
    groups: [
      {
        key: 'reconAdministration',
        labelKey: 'nav.reconAdministration',
        icon: SlidersHorizontal,
        items: [
          // ── Real screens (Reconciliation.Api) — only allowlisted tenants (e.g. 40) ──
          // Order mirrors the mock/"upgrade" menu below: Dashboard, Status, Matching,
          // Approvals first; the real-only Conciliations/Movements screens follow.
          { key: 'reconDashboard', labelKey: 'nav.dashboard', path: '/reconciliation/dashboard', icon: LayoutDashboard, status: 'active', tenantOnly: 'recon-real', searchTagsKey: 'searchTags.reconDashboard' },
          { key: 'reconStatus', labelKey: 'nav.reconStatus', path: '/reconciliation/chain-instances', icon: Activity, status: 'active', tenantOnly: 'recon-real', searchTagsKey: 'searchTags.reconStatus' },
          { key: 'reconMatching1to1', labelKey: 'nav.matching1to1', path: '/reconciliation/matching-1to1', icon: Link, status: 'active', tenantOnly: 'recon-real', searchTagsKey: 'searchTags.reconMatching1to1' },
          { key: 'reconApprovals', labelKey: 'nav.reconApprovals', path: '/reconciliation/requests', icon: CheckCircle, status: 'active', tenantOnly: 'recon-real', searchTagsKey: 'searchTags.reconApprovals' },
          { key: 'reconConciliations', labelKey: 'nav.conciliations', path: '/reconciliation/conciliations', icon: GitMerge, status: 'active', tenantOnly: 'recon-real', searchTagsKey: 'searchTags.reconConciliations' },
          { key: 'reconMovements', labelKey: 'nav.movements', path: '/reconciliation/movements', icon: ArrowLeftRight, status: 'active', tenantOnly: 'recon-real', searchTagsKey: 'searchTags.reconMovements' },
          { key: 'reconMovementGraph', labelKey: 'nav.movementGraph', path: '/reconciliation/movement-graph', icon: Waypoints, status: 'active', tenantOnly: 'recon-real', searchTagsKey: 'searchTags.reconMovementGraph' },
          // ── Mock/"upgrade" screens — every other tenant (mirror the pre-branch menu) ──
          { key: 'reconDashboardMock', labelKey: 'nav.dashboard', path: '/reconciliation/dashboard', icon: LayoutDashboard, status: 'inactive', tenantOnly: 'recon-mock', searchTagsKey: 'searchTags.reconDashboard' },
          { key: 'reconStatusMock', labelKey: 'nav.reconStatus', path: '/reconciliation/flows', icon: Activity, status: 'inactive', tenantOnly: 'recon-mock', searchTagsKey: 'searchTags.reconStatus' },
          { key: 'reconMatching1to1Mock', labelKey: 'nav.matching1to1', path: '/reconciliation/matching-1to1', icon: Link, status: 'inactive', tenantOnly: 'recon-mock', searchTagsKey: 'searchTags.reconMatching1to1' },
          { key: 'reconApprovalsMock', labelKey: 'nav.reconApprovals', path: '/reconciliation/requests', icon: CheckCircle, status: 'inactive', tenantOnly: 'recon-mock', searchTagsKey: 'searchTags.reconApprovals' },
          { key: 'reconBatchMatching', labelKey: 'nav.batchMatching', path: '/reconciliation/batch-matching', icon: Layers, status: 'inactive', tenantOnly: 'recon-mock', searchTagsKey: 'searchTags.reconBatchMatching' },
          { key: 'reconPosting', labelKey: 'nav.reconPosting', path: '/reconciliation/posting', icon: Send, status: 'inactive', tenantOnly: 'recon-mock', searchTagsKey: 'searchTags.reconPosting' },
        ],
      },
      {
        key: 'reconReporting',
        labelKey: 'nav.reconReporting',
        icon: BarChart3,
        items: [
          { key: 'reconConciliationReporting', labelKey: 'nav.conciliationReporting', path: '/reconciliation/conciliation-reporting', icon: FileSpreadsheet, status: 'active', tenantOnly: 'recon-real', searchTagsKey: 'searchTags.reconConciliationReporting' },
          { key: 'reconFeeReport', labelKey: 'nav.feeReport', path: '/reconciliation/fee-report', icon: Receipt, status: 'active', tenantOnly: 'recon-real', searchTagsKey: 'searchTags.reconFeeReport' },
        ],
      },
    ],
  },

  // ── Reporting (mega-menu) ─────────────────────────────
  {
    key: 'reporting',
    labelKey: 'nav.reporting',
    icon: BarChart3,
    groups: [
      // Column 1 — Overview + Cash & Liquidity (merged)
      {
        key: 'rptOverview',
        labelKey: 'nav.rptOverview',
        icon: LayoutDashboard,
        items: [
          { key: 'reportingDashboard', labelKey: 'nav.dashboard', path: '/reporting/dashboard', icon: LayoutDashboard, status: 'inactive', searchTagsKey: 'searchTags.reportingDashboard' },
          { key: 'reportingAuditTrail', labelKey: 'nav.auditTrail', path: '/reporting/audit-trail', icon: ScrollText, status: 'inactive', searchTagsKey: 'searchTags.reportingAuditTrail' },
          { key: 'rptBalanceDifference', labelKey: 'nav.balanceDifference', path: '/reporting/cash-liquidity/balance-difference', icon: Scale, status: 'active', subheaderKey: 'nav.rptCashLiquidity', permission: { name: Permissions.CashPosition, level: PermissionLevel.Read }, searchTagsKey: 'searchTags.rptBalanceDifference' },
          { key: 'rptCategoryPositions', labelKey: 'nav.categoryPositions', path: '/reporting/cash-liquidity/category-positions', icon: ListTree, status: 'active', permission: { name: Permissions.CashPosition, level: PermissionLevel.Read }, searchTagsKey: 'searchTags.rptCategoryPositions' },
          { key: 'rptCashPositionHome', labelKey: 'nav.cashPosition', path: '/reporting/cash-position', icon: LayoutDashboard, status: 'inactive', searchTagsKey: 'searchTags.rptCashPosition' },
          { key: 'rptCashForecast', labelKey: 'nav.cashForecast', path: '/reporting/cash-liquidity/cash-forecast', icon: TrendingUp, status: 'inactive', searchTagsKey: 'searchTags.rptCashForecast' },
          { key: 'rptActualVsForecast', labelKey: 'nav.actualVsForecast', path: '/reporting/cash-liquidity/actual-vs-forecast', icon: BarChart3, status: 'inactive', searchTagsKey: 'searchTags.rptActualVsForecast' },
          { key: 'rptScenarios', labelKey: 'nav.rptScenarios', path: '/reporting/cash-liquidity/scenarios', icon: AlertTriangle, status: 'inactive', searchTagsKey: 'searchTags.rptScenarios' },
          { key: 'rptInfraCashflowDaily',    labelKey: 'nav.rptInfraCashflowDaily',    path: '/reporting/cash-liquidity/cashflow-daily',    icon: TrendingUp,      status: 'inactive', tenantOnly: 'infra', custom: true, searchTagsKey: 'searchTags.rptInfraCashflowDaily' },
          { key: 'rptInfraInterestPayments', labelKey: 'nav.rptInfraInterestPayments', path: '/reporting/cash-liquidity/interest-payments', icon: HandCoins,       status: 'inactive', tenantOnly: 'infra', custom: true, searchTagsKey: 'searchTags.rptInfraInterestPayments' },
          { key: 'rptInfraCashflowForecast', labelKey: 'nav.rptInfraCashflowForecast', path: '/reporting/cash-liquidity/cashflow-forecast', icon: TrendingUp,      status: 'inactive', tenantOnly: 'infra', custom: true, searchTagsKey: 'searchTags.rptInfraCashflowForecast' },
          { key: 'rptInfraDividends',        labelKey: 'nav.rptInfraDividends',        path: '/reporting/cash-liquidity/dividends',         icon: LayoutDashboard, status: 'inactive', tenantOnly: 'infra', custom: true, searchTagsKey: 'searchTags.rptInfraDividends' },
          // Datalake dashboards POC (UC-100)
          { key: 'rptDatalakeCashPosition',  labelKey: 'nav.rptDatalakeCashPosition',  path: '/reporting/datalake/cash-position',    icon: LayoutDashboard, status: 'active', tenantOnly: 'datalake', subheaderKey: 'nav.rptDatalake' },
          { key: 'rptDatalakeBalanceChange', labelKey: 'nav.rptDatalakeBalanceChange', path: '/reporting/datalake/balance-change',   icon: BarChart3,       status: 'active', tenantOnly: 'datalake' },
          { key: 'rptDatalakeStatement',     labelKey: 'nav.rptDatalakeStatement',     path: '/reporting/datalake/account-statement', icon: ScrollText,     status: 'active', tenantOnly: 'datalake' },
          { key: 'rptDatalakeCashFlow',      labelKey: 'nav.rptDatalakeCashFlow',      path: '/reporting/datalake/cash-flow',        icon: TrendingUp,      status: 'active', tenantOnly: 'datalake' },
          { key: 'rptReports', labelKey: 'nav.rptReports', path: '/reporting/reports', icon: Blocks, status: 'active', tenantOnly: 'datalake', visibleFor: canOpenSavedReports },
        ],
      },
      // Column 2 — Operations
      {
        key: 'rptOperations',
        labelKey: 'nav.rptOperations',
        icon: Activity,
        items: [
          { key: 'rptFxContractsBlotter', labelKey: 'nav.rptFxContractsBlotter', path: '/reporting/operations/fx-contracts', icon: FileSignature, status: 'inactive', searchTagsKey: 'searchTags.rptFxContractsBlotter' },
          { key: 'rptLoansBlotter', labelKey: 'nav.rptLoansBlotter', path: '/reporting/operations/loans', icon: HandCoins, status: 'inactive', searchTagsKey: 'searchTags.rptLoansBlotter' },
          { key: 'rptInvestmentsBlotter', labelKey: 'nav.rptInvestmentsBlotter', path: '/reporting/operations/investments', icon: PiggyBank, status: 'inactive', searchTagsKey: 'searchTags.rptInvestmentsBlotter' },
          { key: 'rptTransactions', labelKey: 'nav.transactions', path: '/transactions', icon: ArrowLeftRight, status: 'active', permission: { name: Permissions.Transactions, level: PermissionLevel.Read }, searchTagsKey: 'searchTags.rptTransactions' },
          { key: 'rptInvestmentsMovements', labelKey: 'nav.rptInvestmentsMovements', path: '/reporting/operations/investments-movements', icon: PiggyBank, status: 'inactive', searchTagsKey: 'searchTags.rptInvestmentsMovements', tenantOnly: 'infra' },
          { key: 'rptBankStatements', labelKey: 'nav.bankStatements', path: '/bank-statements', icon: FileText, status: 'active', permission: { name: Permissions.BankStatements, level: PermissionLevel.Read }, searchTagsKey: 'searchTags.bankStatements' },
          { key: 'rptInvoices', labelKey: 'nav.invoices', path: '/invoices', icon: Receipt, status: 'active', permission: { name: Permissions.Invoices, level: PermissionLevel.Read }, searchTagsKey: 'searchTags.invoices' },
          { key: 'rptReconStatus', labelKey: 'nav.rptReconStatus', path: '/reconciliation/chain-instances', icon: GitCompareArrows, status: 'active', searchTagsKey: 'searchTags.rptReconStatus' },
          { key: 'rptDebtRepayment', labelKey: 'nav.rptDebtRepayment', path: '/reporting/operations/debt-repayment', icon: HandCoins, status: 'inactive', searchTagsKey: 'searchTags.rptDebtRepayment' },
          { key: 'rptCustomerAging', labelKey: 'nav.rptCustomerAging', path: '/reporting/operations/customer-aging', icon: Users, status: 'inactive', searchTagsKey: 'searchTags.rptCustomerAging' },
          { key: 'rptGlPostingLifeCycle', labelKey: 'nav.glPostingLifeCycle', path: '/transactions-posting/life-cycle', icon: BookOpenCheck, status: 'inactive', searchTagsKey: 'searchTags.glPostingLifeCycle' },
          { key: 'rptExchangeRates', labelKey: 'nav.exchangeRates', path: '/exchange-rates', icon: TrendingUp, status: 'active', permission: { name: Permissions.ExchangeRates, level: PermissionLevel.Read }, searchTagsKey: 'searchTags.exchangeRates' },
          // UC-099: the same report as Reconciliations › Reporting, listed with the other operations reports.
          { key: 'rptConciliationReporting', labelKey: 'nav.conciliationReporting', path: '/reconciliation/conciliation-reporting', icon: FileSpreadsheet, status: 'active', tenantOnly: 'recon-real', searchTagsKey: 'searchTags.reconConciliationReporting' },
          // UC-102: the same report as Reconciliations › Reporting › Fee Report.
          { key: 'rptFeeReport', labelKey: 'nav.feeReport', path: '/reconciliation/fee-report', icon: Receipt, status: 'active', tenantOnly: 'recon-real', searchTagsKey: 'searchTags.reconFeeReport' },
        ],
      },
      // Column 4 — Risk Management
      {
        key: 'rptRisk',
        labelKey: 'nav.rptRiskManagement',
        icon: AlertTriangle,
        items: [
          { key: 'rptRiskCockpit', labelKey: 'nav.rptRiskCockpit', path: '/reporting/risk/cockpit', icon: LayoutDashboard, status: 'inactive', searchTagsKey: 'searchTags.rptRiskCockpit' },
          { key: 'rptMtm', labelKey: 'nav.rptMtm', path: '/reporting/risk/mtm', icon: TrendingUp, status: 'inactive', searchTagsKey: 'searchTags.rptMtm' },
          { key: 'rptFxExposure', labelKey: 'nav.rptFxExposure', path: '/reporting/risk/fx-exposure', icon: ArrowLeftRight, status: 'inactive', searchTagsKey: 'searchTags.rptFxExposure' },
          { key: 'rptInvestmentRisk', labelKey: 'nav.rptInvestmentRisk', path: '/reporting/risk/investments', icon: PiggyBank, status: 'inactive', searchTagsKey: 'searchTags.rptInvestmentRisk', tenantOnly: 'infra' },
          { key: 'rptCreditRisk', labelKey: 'nav.rptCreditRisk', path: '/reporting/risk/credit-risk', icon: Shield, status: 'inactive', searchTagsKey: 'searchTags.rptCreditRisk' },
          { key: 'rptIrrbb', labelKey: 'nav.rptIrrbb', path: '/reporting/risk/irrbb', icon: Activity, status: 'inactive', searchTagsKey: 'searchTags.rptIrrbb' },
          { key: 'rptAlm', labelKey: 'nav.rptAlm', path: '/reporting/risk/alm', icon: Layers, status: 'inactive', searchTagsKey: 'searchTags.rptAlm' },
        ],
      },
    ],
  },

  // ── Payments ──────────────────────────────────────────
  {
    key: 'payments',
    labelKey: 'nav.payments',
    icon: CreditCard,
    items: [
      {
        key: 'paymentsList',
        labelKey: 'nav.paymentBlotter',
        path: '/payments',
        icon: CreditCard,
        status: 'active',
        permission: { name: Permissions.PaymentsBlotter, level: PermissionLevel.Read },
        searchTagsKey: 'searchTags.paymentsList',
      },
      { key: 'paymentLifeCycleBlotter', labelKey: 'nav.paymentLifeCycleBlotter', path: '/payments/life-cycle-blotter', icon: Banknote, status: 'inactive', searchTagsKey: 'searchTags.paymentLifeCycleBlotter' },
      { key: 'paymentsApproval', labelKey: 'nav.paymentApprovals', path: '/payments/approval', icon: ClipboardCheck, status: 'inactive', searchTagsKey: 'searchTags.paymentsApproval' },
      { key: 'netSettlements', labelKey: 'nav.netSettlements', path: '/payments/net-settlements', icon: Receipt, status: 'inactive', searchTagsKey: 'searchTags.netSettlements' },
    ],
  },

  // ── Data ─────────────────────────────────────────────
  {
    key: 'data',
    labelKey: 'nav.data',
    icon: Database,
    items: [
      { key: 'dataDashboard', labelKey: 'nav.dataHub', path: '/data/dashboard', icon: LayoutDashboard, status: 'active', searchTagsKey: 'searchTags.dataDashboard' },
      { key: 'dataRepository', labelKey: 'nav.dataRepository', path: '/data/repository', icon: Database, status: 'inactive', searchTagsKey: 'searchTags.dataRepository' },
      { key: 'dataExports', labelKey: 'nav.dataExports', path: '/data/exports', icon: Download, status: 'active', searchTagsKey: 'searchTags.dataExports' },
      { key: 'dataFinancialEvents', labelKey: 'nav.financialEvents', path: '/data/financial-events', icon: FileStack, status: 'active', searchTagsKey: 'searchTags.dataFinancialEvents' },
    ],
  },

  // ── Workflows ──────────────────────────────────────────
  {
    key: 'workflows',
    labelKey: 'nav.workflows',
    icon: Workflow,
    items: [
      { key: 'wfDashboard', labelKey: 'nav.workflowDashboard', path: '/workflows/dashboard', icon: LayoutDashboard, status: 'inactive', searchTagsKey: 'searchTags.wfDashboard' },
      { key: 'wfApprovals', labelKey: 'nav.workflowApprovals', path: '/workflows/approvals', icon: CheckCircle, status: 'inactive', searchTagsKey: 'searchTags.wfApprovals' },
    ],
  },

  // ── Alerts ────────────────────────────────────────────
  {
    key: 'alerts',
    labelKey: 'nav.alertsSection',
    icon: BellRing,
    items: [
      { key: 'alertsDashboard', labelKey: 'nav.alertsDashboard', path: '/alerts/dashboard', icon: LayoutDashboard, status: 'inactive', searchTagsKey: 'searchTags.alertsDashboard' },
      { key: 'alertsIntegrations', labelKey: 'nav.alertsIntegrations', path: '/alerts/integrations', icon: Plug, status: 'inactive', searchTagsKey: 'searchTags.alertsIntegrations' },
      { key: 'alertsData', labelKey: 'nav.alertsData', path: '/alerts/data', icon: Database, status: 'inactive', searchTagsKey: 'searchTags.alertsData' },
      { key: 'alertsReconciliation', labelKey: 'nav.alertsReconciliation', path: '/alerts/reconciliation', icon: GitCompareArrows, status: 'inactive', searchTagsKey: 'searchTags.alertsReconciliation' },
      { key: 'alertsGlPosting', labelKey: 'nav.alertsGlPosting', path: '/alerts/gl-posting', icon: BookOpenCheck, status: 'inactive', tenantOnly: 'ecobank', searchTagsKey: 'searchTags.alertsGlPosting' },
      { key: 'alertsPayments', labelKey: 'nav.alertsPayments', path: '/alerts/payments', icon: CreditCard, status: 'inactive', searchTagsKey: 'searchTags.alertsPayments' },
      { key: 'alertsReporting', labelKey: 'nav.alertsReporting', path: '/alerts/reporting', icon: BarChart3, status: 'inactive', searchTagsKey: 'searchTags.alertsReporting' },
      { key: 'alertsPrefunding', labelKey: 'nav.alertsPrefunding', path: '/alerts/prefunding', icon: Wallet, status: 'inactive', searchTagsKey: 'searchTags.alertsPrefunding' },
      { key: 'alertsWorkflows', labelKey: 'nav.alertsWorkflows', path: '/alerts/workflows', icon: Workflow, status: 'inactive', searchTagsKey: 'searchTags.alertsWorkflows' },
      { key: 'alertsAgents', labelKey: 'nav.alertsAgents', path: '/alerts/agents', icon: Bot, status: 'inactive', searchTagsKey: 'searchTags.alertsAgents' },
      { key: 'alertsAdmin', labelKey: 'nav.alertsAdmin', path: '/alerts/admin', icon: Shield, status: 'inactive', searchTagsKey: 'searchTags.alertsAdmin' },
    ],
  },

  // ── Agents ────────────────────────────────────────────
  {
    key: 'agents',
    labelKey: 'nav.agents',
    icon: Bot,
    items: [
      { key: 'agentsDashboard', labelKey: 'nav.agentDashboard', path: '/agents/dashboard', icon: LayoutDashboard, status: 'inactive', searchTagsKey: 'searchTags.agentsDashboard' },
      { key: 'agentsBuilder', labelKey: 'nav.agentBuilder', path: '/agents/builder', icon: Hammer, status: 'inactive', searchTagsKey: 'searchTags.agentsBuilder' },
      { key: 'agentsApprovals', labelKey: 'nav.agentApprovals', path: '/agents/approvals', icon: CheckCircle, status: 'inactive', searchTagsKey: 'searchTags.agentsApprovals' },
      { key: 'agentsChats', labelKey: 'nav.agentChats', path: '/agents/chats', icon: MessageCircle, status: 'inactive', searchTagsKey: 'searchTags.agentsChats' },
      { key: 'agentsLogs', labelKey: 'nav.agentLogs', path: '/agents/logs', icon: ScrollText, status: 'inactive', searchTagsKey: 'searchTags.agentsLogs' },
    ],
  },

  // ── Admin Console (mega-menu: 2 groups) ───────────────
  {
    key: 'adminConsole',
    labelKey: 'nav.adminConsole',
    icon: Shield,
    groups: [
      {
        key: 'adminConsoleGroup',
        labelKey: 'nav.adminConsole',
        icon: Shield,
        items: [
          {
            key: 'userAgentMgm',
            labelKey: 'nav.userAgentManagement',
            path: '/admin/user-agent-mgm',
            icon: Users,
            status: 'inactive',
            searchTagsKey: 'searchTags.userAgentMgm',
          },
          // UC-076: its own screen beside User & Agent Management, which is still experimental.
          {
            key: 'aiUsage',
            labelKey: 'nav.aiUsage',
            path: '/admin/ai-usage',
            icon: Sparkles,
            status: 'active',
            requireAdmin: true,
            searchTagsKey: 'searchTags.aiUsage',
          },
          {
            key: 'accessTokens',
            labelKey: 'nav.accessTokens',
            path: '/admin/access-tokens',
            icon: KeyRound,
            status: 'active',
            permission: { name: Permissions.AccessTokens, level: PermissionLevel.Admin },
            searchTagsKey: 'searchTags.accessTokens',
          },
          {
            key: 'groups',
            labelKey: 'nav.groups',
            path: '/admin/groups',
            icon: UsersRound,
            status: 'active',
            permission: { name: Permissions.UserGroups, level: PermissionLevel.Admin },
            searchTagsKey: 'searchTags.groups',
          },
        ],
      },
      {
        // CU-91 / UC-084: users, roles and their audit trail as one group (TH-459 navigation).
        key: 'usersRolesGroup',
        labelKey: 'nav.usersRoles',
        icon: Users,
        items: [
          {
            key: 'users',
            labelKey: 'nav.userManagement',
            path: '/admin/users',
            icon: Users,
            status: 'active',
            permission: { name: Permissions.UserManagement, level: PermissionLevel.Read },
            searchTagsKey: 'searchTags.users',
          },
          {
            key: 'roles',
            labelKey: 'nav.rolesPermissions',
            path: '/admin/roles',
            icon: ShieldCheck,
            status: 'active',
            permission: { name: Permissions.UserManagement, level: PermissionLevel.Read },
            searchTagsKey: 'searchTags.roles',
          },
          {
            key: 'userAuditTrail',
            labelKey: 'nav.userAuditTrail',
            path: '/admin/audit',
            icon: ScrollText,
            status: 'active',
            permission: { name: Permissions.UserManagement, level: PermissionLevel.Read },
            searchTagsKey: 'searchTags.userAuditTrail',
          },
        ],
      },
      {
        key: 'masterDataGroup',
        labelKey: 'nav.masterData',
        icon: SlidersHorizontal,
        // CS-102 order: ticket items first in requested top-down order,
        // then pre-existing admin items (static data). Users and roles moved to usersRolesGroup (UC-084).
        items: [
          {
            key: 'companyGroups',
            labelKey: 'nav.companyGroups',
            path: '/admin/company-groups',
            icon: Building,
            status: 'active',
            permission: { name: Permissions.CompanyGroups, level: PermissionLevel.Read },
            searchTagsKey: 'searchTags.companyGroups',
          },
          {
            key: 'companies',
            labelKey: 'nav.companies',
            path: '/admin/companies',
            icon: Building2,
            status: 'active',
            permission: { name: Permissions.Companies, level: PermissionLevel.Read },
            searchTagsKey: 'searchTags.companies',
          },
          {
            key: 'bankAccounts',
            labelKey: 'nav.bankAccounts',
            path: '/bank-accounts',
            icon: Landmark,
            status: 'active',
            permission: { name: Permissions.BankAccounts, level: PermissionLevel.Read },
            searchTagsKey: 'searchTags.rptBankAccounts',
          },
          {
            key: 'counterparties',
            labelKey: 'nav.counterparties',
            path: '/admin/counterparties',
            icon: Handshake,
            status: 'active',
            permission: { name: Permissions.Counterparties, level: PermissionLevel.Read },
            searchTagsKey: 'searchTags.counterparties',
          },
          {
            key: 'customEntities',
            labelKey: 'nav.customEntities',
            path: '/data/custom-entities',
            icon: Layers,
            status: 'active',
            // No granular permission exists in core for Custom Entities;
            // gate by the admin flag per the CS-102 product rule.
            requireAdmin: true,
            searchTagsKey: 'searchTags.dataCustomEntities',
          },
          {
            key: 'paymentApprovalRules',
            labelKey: 'nav.paymentApprovalRules',
            path: '/admin/payment-approvals',
            icon: FileSignature,
            status: 'active',
            // Core exposes no granular permission for the approval rules and its write endpoint is
            // admin-only, so the item follows the same rule as Custom Entities.
            requireAdmin: true,
            searchTagsKey: 'searchTags.paymentApprovalRules',
          },
          {
            key: 'staticData',
            labelKey: 'nav.staticData',
            path: '/admin/static-data',
            icon: SlidersHorizontal,
            status: 'inactive',
            searchTagsKey: 'searchTags.staticData',
          },
        ],
      },
    ],
  },

  // ── Settings (mega-menu: 3 groups) ────────────────────
  {
    key: 'settings',
    labelKey: 'nav.settings',
    icon: Settings,
    groups: [
      {
        key: 'settingsIngestion',
        labelKey: 'nav.ingestion',
        icon: FileStack,
        items: [
          {
            key: 'parseTemplates',
            labelKey: 'nav.parseTemplates',
            path: '/parse-templates',
            icon: FileStack,
            status: 'active',
            searchTagsKey: 'searchTags.parseTemplates',
          },
          {
            key: 'classificationTags',
            labelKey: 'nav.classificationTags',
            // Sub-section of the first column (a fifth column does not fit). Tags first: a classification rule
            // assigns tags that have to exist already.
            subheaderKey: 'nav.transactionTagging',
            path: '/admin/tagging/tags',
            icon: Tag,
            status: 'active',
            permission: { name: Permissions.Tags, level: PermissionLevel.Read },
            searchTagsKey: 'searchTags.classificationTags',
          },
          {
            key: 'classificationRules',
            labelKey: 'nav.classificationRules',
            path: '/admin/tagging/rules',
            icon: SlidersHorizontal,
            status: 'active',
            permission: { name: Permissions.Tags, level: PermissionLevel.Read },
            searchTagsKey: 'searchTags.classificationRules',
          },
          {
            key: 'classificationConflicts',
            labelKey: 'nav.classificationConflicts',
            path: '/admin/tagging/conflicts',
            icon: AlertTriangle,
            status: 'active',
            count: { useValue: useOpenConflictPairsCount, ariaLabelKey: 'nav.classificationConflictsCount' },
            permission: { name: Permissions.Tags, level: PermissionLevel.Read },
            searchTagsKey: 'searchTags.classificationConflicts',
          },
        ],
      },
      {
        key: 'settingsWorkflows',
        labelKey: 'nav.workflows',
        icon: Workflow,
        items: [
          { key: 'wfBuilder', labelKey: 'nav.workflowBuilder', path: '/workflows/builder', icon: Hammer, status: 'inactive', searchTagsKey: 'searchTags.wfBuilder' },
        ],
      },
      {
        key: 'settingsReconciliations',
        labelKey: 'nav.reconciliation',
        icon: GitCompareArrows,
        items: [
          // Reconciliation Flows (Workflows screen). Tenant-gated: real-module
          // tenants get the live screen (active); every other tenant gets the
          // mock/"upgrade" variant.
          { key: 'reconWorkflows', labelKey: 'nav.reconWorkflows', path: '/reconciliation/workflows', icon: GitMerge, status: 'active', tenantOnly: 'recon-real', searchTagsKey: 'searchTags.reconWorkflows' },
          { key: 'reconWorkflowsMock', labelKey: 'nav.reconWorkflows', path: '/reconciliation/workflows', icon: GitMerge, status: 'inactive', tenantOnly: 'recon-mock', searchTagsKey: 'searchTags.reconWorkflows' },
          { key: 'conciliationCriteria', labelKey: 'nav.reconciliationCriteria', path: '/reconciliation/conciliation-criteria', icon: SlidersHorizontal, status: 'inactive', tenantOnly: 'recon-mock', searchTagsKey: 'searchTags.conciliationCriteria' },
        ],
      },
      {
        key: 'settingsReports',
        labelKey: 'nav.reports',
        icon: BarChart3,
        items: [
          { key: 'reportingBuilder', labelKey: 'nav.buildYourOwn', path: '/reporting/builder', icon: Hammer, status: 'inactive', searchTagsKey: 'searchTags.reportingBuilder' },
        ],
      },
    ],
  },

]
